import { useMutation, useQuery } from "@tanstack/solid-query";
import { axiosInstance, queryClient } from "@/api/client";
import {
  BILLING_CAMPAIGN_KEY,
  BILLING_INVOICES_KEY,
  BILLING_PLANS_KEY,
  BILLING_STATUS_KEY,
  BILLING_SUBSCRIPTION_KEY,
  BILLING_USAGE_KEY,
} from "@/api/query-keys";
import { getApiErrorMessage } from "@/api/utils/error";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import type {
  BillingInterval,
  BillingStatusContract,
  CampaignResponse,
  CancellationReason,
  CancelSubscriptionResponse,
  CheckoutSessionResponse,
  Invoice,
  Plan,
  PlanSlug,
  PortalSessionInput,
  PortalSessionResponse,
  Subscription,
  UsageContract,
} from "./types";

// ─── Fetchers ────────────────────────────────────────────────────────────────

const getSubscription = async () => {
  const { data } = await axiosInstance.get<{
    subscription: Subscription | null;
  }>("/payments/subscription");
  return data;
};

const getBillingStatus = async () => {
  const { data } = await axiosInstance.get<BillingStatusContract>(
    "/payments/billing-status",
  );
  return data;
};

const getPlans = async () => {
  const { data } = await axiosInstance.get<{ plans: Plan[] }>(
    "/payments/plans",
  );
  return data;
};

const getInvoices = async () => {
  const { data } = await axiosInstance.get<{ invoices: Invoice[] }>(
    "/payments/invoices",
  );
  return data;
};

const createCheckoutSession = async (input: {
  company_id: string;
  plan_slug: PlanSlug;
  billing_interval: BillingInterval;
}) => {
  const { data } = await axiosInstance.post<CheckoutSessionResponse>(
    "/payments/checkout-session",
    input,
  );
  return data;
};

const createPortalSession = async (
  input: { company_id: string } & PortalSessionInput,
) => {
  const { data } = await axiosInstance.post<PortalSessionResponse>(
    "/payments/customer-portal-session",
    input,
  );
  return data;
};

const cancelSubscription = async (input: {
  company_id: string;
  reason: CancellationReason;
  feedback?: string;
}) => {
  const { data } = await axiosInstance.post<CancelSubscriptionResponse>(
    "/payments/cancel",
    input,
  );
  return data;
};

const getUsage = async () => {
  const { data } = await axiosInstance.get<UsageContract>("/payments/usage");
  return data;
};

const getCampaign = async () => {
  const { data } =
    await axiosInstance.get<CampaignResponse>("/payments/campaign");
  return data;
};

// ─── Query configs ────────────────────────────────────────────────────────────

export const createSubscriptionQueryConfig = () => ({
  queryKey: [BILLING_SUBSCRIPTION_KEY],
  queryFn: () => getSubscription(),
  staleTime: 5 * 60 * 1000,
});

export const createBillingStatusQueryConfig = () => ({
  queryKey: [BILLING_STATUS_KEY],
  queryFn: () => getBillingStatus(),
  staleTime: 5 * 60 * 1000,
});

export const createPlansQueryConfig = () => ({
  queryKey: [BILLING_PLANS_KEY],
  queryFn: () => getPlans(),
  staleTime: Number.POSITIVE_INFINITY,
});

export const createInvoicesQueryConfig = () => ({
  queryKey: [BILLING_INVOICES_KEY],
  queryFn: () => getInvoices(),
});

// ─── Query hooks ──────────────────────────────────────────────────────────────

export const useSubscription = () => {
  const [auth] = useAuthV2();
  const [store] = useCompanyStore();
  return useQuery(() => ({
    ...createSubscriptionQueryConfig(),
    enabled: !!auth.profile?.id && !!store.selectedCompany?.id,
  }));
};

export const useBillingStatus = () => {
  const [auth] = useAuthV2();
  const [store] = useCompanyStore();
  return useQuery(() => ({
    ...createBillingStatusQueryConfig(),
    enabled: !!auth.profile?.id && !!store.selectedCompany?.id,
  }));
};

export const usePlans = () => {
  return useQuery(() => createPlansQueryConfig());
};

export const useInvoices = () => {
  const [auth] = useAuthV2();
  const [store] = useCompanyStore();
  return useQuery(() => ({
    ...createInvoicesQueryConfig(),
    enabled: !!auth.profile?.id && !!store.selectedCompany?.id,
  }));
};

// ─── Mutation hooks ───────────────────────────────────────────────────────────

export const useCreateCheckoutSession = () => {
  const [store] = useCompanyStore();
  return useMutation(() => ({
    mutationFn: (input: {
      plan_slug: PlanSlug;
      billing_interval: BillingInterval;
    }) => {
      const company_id = store.selectedCompany?.id ?? "";
      return createCheckoutSession({ company_id, ...input });
    },
    onError: (error: unknown) => {
      console.error(
        getApiErrorMessage(error, "Impossibile avviare il checkout"),
      );
    },
  }));
};

export const useCreatePortalSession = () => {
  const [store] = useCompanyStore();
  return useMutation(() => ({
    mutationFn: (input?: PortalSessionInput) => {
      const company_id = store.selectedCompany?.id ?? "";
      return createPortalSession({ company_id, ...input });
    },
    onError: (error: unknown) => {
      console.error(
        getApiErrorMessage(
          error,
          "Impossibile aprire il portale di fatturazione",
        ),
      );
    },
  }));
};

export const createUsageQueryConfig = () => ({
  queryKey: [BILLING_USAGE_KEY],
  queryFn: () => getUsage(),
  staleTime: 2 * 60 * 1000,
  // Entitlement gate: always re-check the live count when a guarded surface
  // mounts, so the quota guard never reflects a stale used/limit after the user
  // creates or (de)activates an offer through a path that misses invalidation.
  refetchOnMount: "always" as const,
  retry: false,
});

export const useUsage = () => {
  const [auth] = useAuthV2();
  const [store] = useCompanyStore();
  return useQuery(() => ({
    ...createUsageQueryConfig(),
    enabled: !!auth.profile?.id && !!store.selectedCompany?.id,
  }));
};

// Early-adopter campaign. Authenticated-only (401 without token); the −50% is
// applied server-side at checkout. We degrade gracefully (no retry) so an absent
// or errored campaign simply yields standard pricing. See CONTRACT.md.
export const createCampaignQueryConfig = () => ({
  queryKey: [BILLING_CAMPAIGN_KEY],
  queryFn: () => getCampaign(),
  staleTime: 5 * 60 * 1000,
  retry: false,
});

export const useCampaign = () => {
  const [auth] = useAuthV2();
  return useQuery(() => ({
    ...createCampaignQueryConfig(),
    enabled: !!auth.profile?.id,
  }));
};

export const useCancelSubscription = () => {
  const [store] = useCompanyStore();
  return useMutation(() => ({
    mutationFn: (input: { reason: CancellationReason; feedback?: string }) => {
      const company_id = store.selectedCompany?.id ?? "";
      return cancelSubscription({ company_id, ...input });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [BILLING_SUBSCRIPTION_KEY] }),
        queryClient.refetchQueries({ queryKey: [BILLING_STATUS_KEY] }),
      ]);
    },
    onError: (error: unknown) => {
      console.error(
        getApiErrorMessage(error, "Impossibile annullare l'abbonamento"),
      );
    },
  }));
};
